<template>
  <v-container fluid class="pa-4">
    <!-- Greeting -->
    <v-row align="center" class="mb-2">
      <v-col>
        <div class="text-h5 font-weight-bold">Welcome back</div>
        <div class="text-caption text--secondary">{{ todayLabel }}</div>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <span v-if="lastUpdated" class="text-caption text--secondary mr-3">
          Updated {{ lastUpdatedFromNow }}
        </span>
        <v-select
          v-model="autoRefreshInterval"
          :items="refreshIntervals"
          label="Auto-refresh"
          dense
          hide-details
          outlined
          style="max-width: 160px"
          class="mr-2"
        />
        <v-btn icon @click="getSampleStats" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- KPI tiles -->
    <v-row dense>
      <v-col
        v-for="kpi in kpis"
        :key="kpi.label"
        cols="12"
        sm="6"
        md="3"
      >
        <base-card class="pa-4" style="height: 100%">
          <div class="d-flex align-center">
            <v-avatar :color="`${kpi.color} lighten-4`" size="44" class="mr-3">
              <v-icon :color="kpi.color">{{ kpi.icon }}</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-caption text--secondary">{{ kpi.label }}</div>
              <div class="text-h5 font-weight-bold">{{ kpi.value }}</div>
              <div v-if="kpi.hint" class="text-caption text--secondary">
                {{ kpi.hint }}
              </div>
            </div>
          </div>
        </base-card>
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row>
      <!-- Samples -->
      <v-col cols="12" md="8">
        <base-card style="height: 100%">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-flask</v-icon>
            Samples
            <v-spacer />
            <v-btn-toggle
              v-if="availableSamplePeriods.length"
              v-model="samplesPeriod"
              mandatory
              dense
              color="primary"
            >
              <v-btn
                v-for="p in availableSamplePeriods"
                :key="p"
                :value="p"
                small
              >
                {{ periodLabel(p) }}
              </v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <apexchart
              v-if="samplesSeries"
              type="area"
              height="320"
              :options="samplesOptions"
              :series="samplesSeries"
            />
            <v-skeleton-loader
              v-else-if="loading"
              type="image"
              height="320"
            />
            <div v-else class="text--secondary text-center py-10">
              <v-icon large color="grey lighten-1">mdi-chart-line</v-icon>
              <div class="mt-2">No samples data yet</div>
            </div>
          </v-card-text>
        </base-card>
      </v-col>

      <!-- Analyzes -->
      <v-col cols="12" md="4">
        <base-card style="height: 100%">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2" color="success">mdi-chart-donut</v-icon>
            Analyzes
          </v-card-title>
          <v-card-text
            v-if="!hasAnalyzes && !loading"
            class="text--secondary text-center py-10"
          >
            <v-icon large color="grey lighten-1">mdi-chart-donut</v-icon>
            <div class="mt-2">No analyzes data yet</div>
          </v-card-text>
          <v-card-text v-else-if="loading && !hasAnalyzes">
            <v-skeleton-loader type="image" height="260" />
          </v-card-text>
          <v-card-text v-else>
            <apexchart
              type="donut"
              height="260"
              :options="analyzesOptions"
              :series="analyzesSeries"
            />
          </v-card-text>
        </base-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { mapGetters, mapActions } from "vuex";

const PERIOD_LABELS = {
  hour: "Today by hour",
  today: "Today",
  week: "Week",
  month: "Month",
};

const sum = (arr) =>
  Array.isArray(arr) ? arr.reduce((s, n) => s + (Number(n) || 0), 0) : 0;

export default {
  name: "AdminDashboard",
  components: {
    apexchart: VueApexCharts,
  },
  data: () => ({
    loading: false,
    samplesPeriod: "hour",
    lastUpdated: null,
    now: Date.now(),
    nowTimer: null,
    autoRefreshInterval: 0,
    autoRefreshTimer: null,
    refreshIntervals: [
      { text: "Off", value: 0 },
      { text: "30 s", value: 30 },
      { text: "1 min", value: 60 },
      { text: "5 min", value: 300 },
    ],
  }),
  computed: {
    ...mapGetters("samples", ["A_stats"]),
    stats() {
      return this.A_stats && !Array.isArray(this.A_stats) ? this.A_stats : {};
    },
    samplesStats() {
      return this.stats.stats_samples || null;
    },
    availableSamplePeriods() {
      const s = this.samplesStats || {};
      return ["hour", "today", "week", "month"].filter(
        (k) => s[k] && s[k].series && Array.isArray(s[k].series.data)
      );
    },
    currentSamples() {
      const s = this.samplesStats;
      if (!s) return null;
      return s[this.samplesPeriod] || null;
    },
    samplesSeries() {
      const cur = this.currentSamples;
      if (!cur || !cur.series) return null;
      return [{ name: "Samples", data: cur.series.data }];
    },
    samplesOptions() {
      const cur = this.currentSamples;
      const baseOptions = cur && cur.options ? cur.options : {};
      return {
        ...baseOptions,
        chart: {
          ...(baseOptions.chart || {}),
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "inherit",
        },
        stroke: { curve: "smooth", width: 2 },
        dataLabels: { enabled: false },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        grid: { borderColor: "#eee", strokeDashArray: 4 },
        colors: ["#1976d2"],
        xaxis: {
          ...(baseOptions.xaxis || {}),
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        tooltip: { theme: this.$vuetify.theme.dark ? "dark" : "light" },
      };
    },
    hasAnalyzes() {
      return !!this.stats.stats_analyzes;
    },
    analyzesSeries() {
      const a = this.stats.stats_analyzes;
      if (!a) return [];
      if (a.series && Array.isArray(a.series.data)) return a.series.data;
      return [];
    },
    analyzesOptions() {
      const a = this.stats.stats_analyzes;
      const labels =
        a && a.labels
          ? a.labels
          : a && a.options && a.options.labels
          ? a.options.labels
          : [];
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        labels,
        legend: { position: "bottom" },
        dataLabels: { enabled: true },
        colors: ["#1976d2", "#43a047", "#fb8c00", "#e53935", "#8e24aa"],
        stroke: { width: 0 },
        tooltip: { theme: this.$vuetify.theme.dark ? "dark" : "light" },
      };
    },
    kpis() {
      const s = this.samplesStats || {};
      const todayData = s.hour && s.hour.series ? s.hour.series.data : null;
      const weekData = s.week && s.week.series ? s.week.series.data : null;
      const monthData = s.month && s.month.series ? s.month.series.data : null;

      let peakHourLabel = "—";
      if (todayData && todayData.length) {
        const max = Math.max(...todayData);
        if (max > 0) {
          const idx = todayData.indexOf(max);
          peakHourLabel = `${idx.toString().padStart(2, "0")}:00`;
        }
      }

      return [
        {
          label: "Samples today",
          value: todayData ? sum(todayData) : "—",
          icon: "mdi-flask",
          color: "primary",
        },
        {
          label: "This week",
          value: weekData ? sum(weekData) : "—",
          icon: "mdi-calendar-week",
          color: "success",
        },
        {
          label: "This month",
          value: monthData ? sum(monthData) : "—",
          icon: "mdi-calendar-month",
          color: "info",
        },
        {
          label: "Peak hour today",
          value: peakHourLabel,
          icon: "mdi-chart-bell-curve",
          color: "warning",
          hint: todayData ? `${sum(todayData)} samples total` : null,
        },
      ];
    },
    todayLabel() {
      return new Date().toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
    lastUpdatedFromNow() {
      if (!this.lastUpdated) return "";
      const diff = Math.max(0, Math.floor((this.now - this.lastUpdated) / 1000));
      if (diff < 5) return "just now";
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      return `${Math.floor(diff / 3600)}h ago`;
    },
  },
  watch: {
    availableSamplePeriods: {
      immediate: true,
      handler(periods) {
        if (periods.length && !periods.includes(this.samplesPeriod)) {
          this.samplesPeriod = periods[0];
        }
      },
    },
    autoRefreshInterval() {
      this.startAutoRefresh();
    },
  },
  methods: {
    ...mapActions("samples", ["list_A_stats"]),
    async getSampleStats() {
      this.loading = true;
      try {
        await this.list_A_stats();
        this.lastUpdated = Date.now();
      } finally {
        this.loading = false;
      }
    },
    periodLabel(p) {
      return PERIOD_LABELS[p] || p;
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      if (this.autoRefreshInterval > 0) {
        this.autoRefreshTimer = setInterval(
          this.getSampleStats,
          this.autoRefreshInterval * 1000
        );
      }
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },
  },
  created() {
    this.getSampleStats();
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    if (this.nowTimer) clearInterval(this.nowTimer);
  },
};
</script>
